import {
  BASE_DOCUMENT_ACK_OPTIONS,
  NIC_DOCUMENT_ACK,
  STUDENT_DOCUMENT_ACK_OPTIONS,
  MEDICAL_CLEARANCE_ACK,
  MEDICAL_CONDITIONS,
  PARQ_QUESTIONS,
} from "./hpcFormConfig";

const DOCUMENT_ACK_OPTIONS = [
  NIC_DOCUMENT_ACK,
  ...BASE_DOCUMENT_ACK_OPTIONS,
  { key: MEDICAL_CLEARANCE_ACK.key, label: MEDICAL_CLEARANCE_ACK.labelRequired },
  ...STUDENT_DOCUMENT_ACK_OPTIONS,
];

const FIELD_LABELS = {
  fullName: "Full name",
  dateOfBirth: "Date of birth",
  age: "Age",
  gender: "Gender",
  nicPassport: "NIC / Passport number",
  nationality: "Nationality",
  occupation: "Occupation",
  address: "Address",
  mobileNumber: "Mobile number",
  whatsappNumber: "WhatsApp number",
  email: "Email address",
  emergencyContactPerson: "Emergency contact name",
  emergencyContactRelationship: "Emergency contact relationship",
  emergencyContactNumber: "Emergency contact number",
  membershipType: "Membership package",
  trainingGoals: "Training goals",
  preferredTrainingTimes: "Preferred training time",
  membershipStartDate: "Membership start date",
  trainerPreference: "Trainer preference",
  takingMedication: "Medication question",
  medicationDetails: "Medication details",
  hasInjuries: "Injuries or limitations question",
  injuryDetails: "Injury details",
  doctorRecommendations: "Doctor's recommendations",
  liabilityAgreed: "Terms and conditions",
  paymentMethod: "Payment method",
  membershipFee: "Membership fee",
  registrationFee: "Registration fee",
  totalAmountPaid: "Total amount paid",
  paymentDate: "Payment date",
  receiptNumber: "Receipt number",
  mediaConsent: "Media consent",
  declarationName: "Member name",
  declarationSignature: "Member signature",
  declarationDate: "Declaration date",
  documentsAck: "Required documents",
  medicalConditions: "Health questions",
  parqAnswers: "PAR-Q questions",
};

const GENERIC_MESSAGES = new Set([
  "Required",
  "This field is required",
  "Invalid or missing value",
  "Answer must be yes or no",
  "Invalid selection",
  "Select at least one option",
  "Document acknowledgments are required",
  "Must be an object with yes/no answers",
  "Invalid JSON body",
  "Validation failed",
  "Some registration details are missing or need to be corrected.",
  "Invalid amount",
  "Must be zero or greater",
  "Invalid age",
]);

const humanizeSegment = (segment) =>
  segment
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const labelForErrorPath = (path) => {
  if (!path) return "This field";

  if (FIELD_LABELS[path]) return FIELD_LABELS[path];

  const medicalMatch = path.match(/^medicalConditions\.(.+)$/);
  if (medicalMatch) {
    const item = MEDICAL_CONDITIONS.find((m) => m.key === medicalMatch[1]);
    if (item) return item.label;
  }

  const parqMatch = path.match(/^parqAnswers\.(.+)$/);
  if (parqMatch) {
    const item = PARQ_QUESTIONS.find((q) => q.key === parqMatch[1]);
    if (item) return item.label;
  }

  const docMatch = path.match(/^documentsAck\.(.+)$/);
  if (docMatch) {
    const item = DOCUMENT_ACK_OPTIONS.find((d) => d.key === docMatch[1]);
    if (item) return item.label;
  }

  return humanizeSegment(path.split(".").pop());
};

const friendlyMissingMessage = (path) => {
  const label = labelForErrorPath(path);

  if (path.startsWith("medicalConditions.")) {
    return `Please answer "${label}" with Yes or No.`;
  }
  if (path.startsWith("parqAnswers.")) {
    return `Please answer "${label}" with Yes or No.`;
  }
  if (path.startsWith("documentsAck.")) {
    return `Please confirm you will provide: ${label}.`;
  }
  if (path === "trainingGoals") {
    return "Please select at least one training goal.";
  }
  if (path === "preferredTrainingTimes") {
    return "Please select at least one preferred training time.";
  }
  if (path === "membershipType") {
    return "Please select a membership package.";
  }
  if (path === "membershipFee" || path === "registrationFee" || path === "totalAmountPaid") {
    return "Please go back to the Membership step and select a package.";
  }
  if (path === "liabilityAgreed") {
    return "Please accept the terms and conditions to continue.";
  }
  if (path === "mediaConsent") {
    return "Please choose whether photos and videos may be used for promotion.";
  }

  return `Please enter your ${label.toLowerCase()}.`;
};

export const humanizeErrorMessage = (path, message) => {
  if (!message) return friendlyMissingMessage(path);
  if (GENERIC_MESSAGES.has(message)) return friendlyMissingMessage(path);
  if (message.startsWith("Maximum length is")) {
    return `${labelForErrorPath(path)} is too long. Please shorten it.`;
  }
  return message;
};

export const flattenErrors = (errors, prefix = "") => {
  if (!errors || typeof errors !== "object") return [];

  if (typeof errors === "string") {
    return [{ path: prefix, message: errors }];
  }

  return Object.entries(errors).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return flattenErrors(value, path);
    }
    return [{ path, message: value }];
  });
};

export const summarizeStepErrors = (errors) => {
  const flat = flattenErrors(errors);
  if (flat.length === 0) {
    return "Please complete the highlighted fields before continuing.";
  }

  const labels = [...new Set(flat.map(({ path }) => labelForErrorPath(path)))];
  if (labels.length === 1) {
    return humanizeErrorMessage(flat[0].path, flat[0].message);
  }
  if (labels.length <= 4) {
    return `Please complete: ${labels.join(", ")}.`;
  }
  return `Please complete ${labels.length} missing items on this step, including ${labels.slice(0, 3).join(", ")}.`;
};

const looksLikeSystemError = (message) =>
  /(mysql|sql|traceback|exception|errno|column|table|syntax|undefined)/i.test(message);

export const formatRegistrationApiError = (data) => {
  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.entries(data.errors).map(([key, message]) =>
      humanizeErrorMessage(key, Array.isArray(message) ? message[0] : message),
    );
    const unique = [...new Set(messages)];
    if (unique.length === 1) return unique[0];
    if (unique.length <= 3) return unique.join(" ");
    return `${unique.slice(0, 2).join(" ")} Please review the form and try again.`;
  }

  if (data?.error) {
    if (looksLikeSystemError(data.error)) {
      return "We could not complete your registration right now. Please try again shortly.";
    }
    return humanizeErrorMessage("_form", data.error);
  }

  return "We could not complete your registration. Please review your details and try again.";
};
